import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilState } from 'recoil';

import { useFormPackets } from 'lib/hooks/queries/forms/useFormPackets';
import { useCustomForm } from 'lib/hooks/queries/customForms/useCustomForm';
import { useFormBuilderContext } from 'lib/context/FormBuilder/FormBuilderContext';
import { TableContextProvider } from 'lib/context/TableContext/TableContextProvider';
import { useCustomFormTypes } from 'lib/hooks/queries/customForms/useCustomFormTypes';
import { FormBuilderContextProvider } from 'lib/context/FormBuilder/FormBuilderContextProvider';

import practiceState from 'components/practice/practiceState';
import AgTablePage from 'components/shared/AgTablePage/AgTablePage';
import WizardModal from 'components/shared/Modal/WizardModal/WizardModal';

import ViewForm from './components/ViewForm';
import { MOTION_COLUMNS } from './lib/helpers';
import Review from './components/Review/Review';
import Builder from './components/Builder/Builder';
import FooterButtons from './components/FooterButtons';
import MotionActions from './components/MotionActions';
import { DEFAULT_FILTERS } from './lib/defaultFilters';
import NameAndType from './components/NameAndType/NameAndType';
import EmptyFormsPlaceholder from './components/EmptyFormsPlaceholder';
import AssignToPacket from './components/AssignToPacket/AssignToPacket';

const OnboardingClinicalForms = ({
  setHeaderButtonProps,
  addFooterActions,
  mutateOrchestratorData,
  orchestratorLoading
}) => {
  useEffect(() => {
    addFooterActions([
      {
        text: 'Continue',
        onClick: () => mutateOrchestratorData({}),
        isDisabled: orchestratorLoading
      }
    ]);
  }, [setHeaderButtonProps, addFooterActions]);

  return (
    <FormBuilderContextProvider>
      <TableContextProvider cols={[]} name="forms">
        <FormBuilderProcess setHeaderButtonProps={setHeaderButtonProps} />
      </TableContextProvider>
    </FormBuilderContextProvider>
  );
};

const FormBuilderProcess = ({ setHeaderButtonProps }) => {
  const [formToView, setFormToView] = useState({});
  const [viewFormModal, setViewFormModal] = useState(false);
  const { id } = useParams();
  const [currentPractice] = useRecoilState(practiceState.currentPractice);

  const practiceId = !!id ? id : currentPractice?.id;

  const {
    autoSave,
    saveButtonText,
    breadCrumbs,
    formik,
    setBreadCrumbs,
    openModal,
    setOpenModal,
    handleClose,
    debouncedSearchTerm: debouncedPacketSearchTerm,
    handleSave,
    isFormSavingLoading,
    showNavigationButtons,
    isEdit,
    handleAutoSave
  } = useFormBuilderContext();

  const { data: formTypesList } = useCustomFormTypes({ params: { practiceId } });

  const { data: formPackets } = useFormPackets({
    params: {
      practice_id: practiceId,
      withGraphFetched: { providers: true, services: true, allForms: true },
      filters: { name: debouncedPacketSearchTerm }
    },
    dependencies: [debouncedPacketSearchTerm]
  });

  const typeOptions = useMemo(() => {
    if (formTypesList?.form_functions?.length > 0) {
      return formTypesList.form_functions.map((f) => ({ label: f.name, value: f.id, meta: f }));
    }
    return [];
  }, [formTypesList]);

  const actionViews = {
    name_and_type: {
      component: <NameAndType formik={formik} currentBreadcrumb="name_and_type" />,
      footer: showNavigationButtons ? (
        <FooterButtons
          buttonText="Go to builder"
          nextTo="builder"
          setBreadCrumbs={setBreadCrumbs}
        />
      ) : (
        <FooterButtons />
      )
    },
    builder: {
      component: <Builder formik={formik} currentBreadcrumb="builder" formTypes={typeOptions} />,
      footer: !isEdit ? (
        <FooterButtons
          buttonText="Complete & review"
          nextTo="review"
          backTo="name_and_type"
          setBreadCrumbs={setBreadCrumbs}
        />
      ) : (
        <FooterButtons buttonText="Submit" isLastStep setBreadCrumbs={setBreadCrumbs} />
      )
    },
    review: {
      component: <Review currentBreadcrumb="review" formTypes={typeOptions} />,
      footer: (
        <FooterButtons
          buttonText="Add to a form packet"
          nextTo="assign_to_packet"
          backTo="builder"
          showSaveAndExit
          setBreadCrumbs={setBreadCrumbs}
        />
      )
    },
    assign_to_packet: {
      component: <AssignToPacket currentBreadcrumb="assign_to_packet" formPackets={formPackets} />,
      footer: (
        <FooterButtons
          buttonText="Submit"
          backTo="review"
          isLastStep
          setBreadCrumbs={setBreadCrumbs}
          showSkip
        />
      )
    }
  };

  const getCurrentView = () => {
    const currentView = breadCrumbs?.find((row) => row.active).view;
    return actionViews[currentView].component;
  };

  const getCurrentFooter = () => {
    const currentView = breadCrumbs?.find((row) => row.active).view;
    return actionViews[currentView].footer;
  };
  const onCellClicked = async (e) => {
    if (e) {
      setFormToView(e);
      setViewFormModal(true);
    }
  };

  const renderEmptyState = useCallback(() => <EmptyFormsPlaceholder />, []);

  return (
    <>
      <AgTablePage
        defaultColumns={MOTION_COLUMNS}
        defaultFilters={DEFAULT_FILTERS}
        useData={useCustomForm}
        title="Form"
        name="forms"
        noHeader
        noRowsOverlayComponent={renderEmptyState}
        onSelectRow={(e) => onCellClicked(e)}
        actions={(props) => (
          <MotionActions
            {...props}
            handleOpen={setOpenModal}
            setMotionHeaderButtonProps={setHeaderButtonProps}
          />
        )}
        rowStyle={{ cursor: 'pointer' }}
        queryParams={{ practice_id: practiceId }}
        autoHeight
        className="overflow-y-auto rounded-2xl border border-solid border-neutral-200"
      />
      {openModal && (
        <WizardModal
          isOpen={openModal}
          handleClose={handleClose}
          handleSaveProgress={handleSave}
          saveProgressLoading={isFormSavingLoading}
          handleAutoSave={handleAutoSave}
          showAutoSave={true}
          showSaveProgress={true}
          title="Form builder"
          autoSave={autoSave}
          saveButtonText={saveButtonText}
          breadcrumbs={breadCrumbs}
          setBreadCrumbs={setBreadCrumbs}
          footerButtons={getCurrentFooter()}>
          {getCurrentView()}
        </WizardModal>
      )}
      {viewFormModal && (
        <ViewForm
          form={formToView}
          setViewFormModal={setViewFormModal}
          viewFormModal={viewFormModal}
        />
      )}
    </>
  );
};

export default OnboardingClinicalForms;
